import { Children, type ReactNode, type ComponentProps } from 'react';
import { Callout, ChangeOutcome, TechnicalDetails } from '@beyond-js/ui/react';
import { ApiProblem } from '@cdn/api';
import { useI18n } from '@cdn/i18n';
import type { Tone } from './badge';
import { Code } from './code';
import { Outages } from './outages';
import styles from './styles.module.css';
import { Refusal } from './unavailable';

const callouts = { neutral: 'info', info: 'info', success: 'success', warning: 'warning', error: 'danger' } as const;

/** What a callout's "Technical details" hold: the source's words, the request and the time (D43) */
type Technical = { text?: string; request?: string; time?: string };
type NoticeProps = { tone?: Tone; title?: string; children?: ReactNode; action?: ReactNode; details?: Technical };

/**
 * A message in the flow of the page, drawn as the family's callout. Errors are announced immediately,
 * everything else politely. Its "Technical details" are their own part of the callout's body, after its words
 * and actions, as the shared callout draws them, so a callout that ends with them keeps one inset
 * to their words (D184).
 */
export function Notice(props: NoticeProps) {
	return props.details ? <Detailed {...props} /> : <Message {...props} />;
}

/** Localization is needed only when the notice contains technical evidence. */
function Detailed(props: NoticeProps) {
	const { locale } = useI18n();
	return <Message {...props} evidence={{ ...props.details, locale: locale.code, labels: TechnicalDetails.labels[locale.code === 'es' ? 'es' : 'en'] }} />;
}

/** The shared message keeps the wrapper's existing children and action contract. */
function Message({ tone = 'info', title, children, action, evidence }: NoticeProps & { evidence?: ComponentProps<typeof Callout>['details'] }) {
	const words = Children.toArray(children).length > 0;
	return <Callout tone={callouts[tone]} title={title} live actions={action} details={evidence}>
		{words && <div className={styles.words}>{children}</div>}
	</Callout>;
}

/**
 * Explains a failed request in the selected language and keeps the raw code and the technical message beside
 * it for troubleshooting. The code is an identifier: it is never translated. A change that met an outage is said in
 * the warning tone and announced politely (decision D186), titled by who did not answer: answered as unavailable,
 * nothing was changed and the person tries again with the operation's own action; with no answer, it may have been
 * made, so the person checks before sending it again. Neither is ever sent again by itself. A refusal because of the
 * person's role, admission or the organization's plan is not a failure: it is stated in place as unavailable
 * (decision D06), with who can change it. A session that ended is not a failure of the request either: the
 * page's `Session` answers it, so its quiet problem shows nothing, or the line saying a destructive action was
 * not sent. A read the service did not answer is not said here: the page's one outage notice says it (decision
 * D163, `ResourceView`), so what is left here is a change's answer and any other failure.
 */
export function ProblemNotice({ problem, action }: { problem: unknown; action?: ReactNode }) {
	const { t, translator } = useI18n();
	const read = ApiProblem.of(problem);
	// A session that ended is answered by the page's `Session`, never here: beside a destructive action that was
	// not sent the person is told to try it again, and anything else stays quiet
	if (read.quiet) return read.unsent ? <p role="status" className="text-sm text-muted">{read.unsent}</p> : null;
	if (Refusal.covers(problem)) return <Refusal problem={problem} />;
	const outcome = Outages.change(read);
	if (outcome) return <Unanswered problem={read} outcome={outcome} action={action} />;
	return (
		<Notice tone="error" title={translator.error(read.code)} action={action} details={{ text: `${read.code}: ${read.message}`, request: read.trace.request, time: read.trace.time }}>
			<p className="text-sm text-muted">
				{t('ui.problem.code')} <Code>{read.code}</Code>
			</p>
		</Notice>
	);
}

/**
 * A change that met an outage (decision D186), said with the family's `ChangeOutcome` in the warning tone: who did
 * not answer and "Nothing was changed.", or, when no answer came, that it may have been made and is checked before
 * trying again. Its evidence is the Technical details, the request identifier among them.
 */
function Unanswered({ problem, outcome, action }: { problem: ApiProblem; outcome: 'unavailable' | 'unknown'; action?: ReactNode }) {
	const { t, locale } = useI18n();
	const code = locale.code === 'es' ? 'es' : 'en';
	const details = { text: `${problem.code}: ${problem.message}`, request: problem.trace.request ?? null, time: problem.trace.time, locale: locale.code, labels: TechnicalDetails.labels[code] };
	return (
		<div className="space-y-2">
			<ChangeOutcome outcome={outcome} cause={Outages.say(Outages.who(problem), t)} details={details} labels={ChangeOutcome.labels[code]} />
			{action}
		</div>
	);
}
